import { useEffect } from 'react';

const SITE_NAME = 'Riymay';
const SITE_URL = 'https://riymay.com';
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Sets this page's title, meta description, Open Graph/Twitter tags, and a
 * self-referencing canonical URL (query strings stripped, so a sorted or
 * filtered variant of a page is never treated as a separate page from the
 * plain one).
 *
 * `title` is the complete tab title this page should show (e.g. "Shipping
 * Policy | Riymay") -- callers write it out in full rather than having it
 * built here, so the homepage can read "Riymay — Premium Unisex Clothing"
 * instead of always being forced into a "<page> | Riymay" shape.
 *
 * This runs client-side, but scripts/prerender.js captures the DOM after it
 * runs, so crawlers and link-preview bots (Google, WhatsApp, Instagram) get
 * these tags baked into the static HTML on the live site, not just real
 * visitors with JS enabled.
 */
export default function useSeo({ title, description, image, path }) {
  useEffect(() => {
    const fullTitle = title || SITE_NAME;
    const canonicalPath = path || window.location.pathname;

    document.title = fullTitle;
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:image', image || DEFAULT_IMAGE);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:url', `${SITE_URL}${canonicalPath}`);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image || DEFAULT_IMAGE);
    upsertCanonical(`${SITE_URL}${canonicalPath}`);
  }, [title, description, image, path]);
}
